import SettingsPageLayout, { settingsPageTitleClass } from "../../../components/shared/SettingsPageLayout";
import React, { useState } from 'react'
import axios from 'axios';
import IntegrationsTableRow from './IntegrationsTableRow'
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingIcon from "@mui/icons-material/Pending";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ListItemIcon, Menu, MenuItem } from "@mui/material";
import ConnectPOSModal from './ConnectPOSModal';
import WarningIcon from "@mui/icons-material/Warning";
import ImportStoresModal from "./ImportStoresModal";
import ConnectedPOSDetailsModal from "./ConnectedPOSDetailsModal";
import { API_create_toast_credential_link } from "../../../constants";
import { getDefaultAxiosConfig } from "../requests/utilities/requests";

// POS Integration Status Constants
const POS_INTEGRATION_NOT_INTEGRATED = 'not_integrated';
const POS_INTEGRATION_TOAST_PENDING = 'toast_pending';
const POS_INTEGRATION_TOAST_CONNECTED = 'toast_connected';

const POS_INTEGRATION_HEARTLAND_RETAIL_PENDING = 'heartland_retail_pending';
const POS_INTEGRATION_HEARTLAND_RETAIL_CONNECTED = 'heartland_retail_connected';

const POS_INTEGRATION_SQUARE_PENDING = 'square_pending';
const POS_INTEGRATION_SQUARE_CONNECTED = 'square_connected';


function IntegrationsDisplay(props) {
  const recipientEmailId = React.useId();
  const actionsMenuId = React.useId();
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [showCredentialLink, setShowCredentialLink] = useState(false);
  const credentialRequestGeneration = React.useRef(0);
  const [showConnectPOSModal, setShowConnectPOSModal] = useState(false);
  const [posModalOpen, setPosModalOpen] = useState(false);
  const [connectedDetailsOpen, setConnectedDetailsOpen] = useState(false);
  const [credentialRecipientEmail, setCredentialRecipientEmail] = useState("");
  const [credentialMagicLink, setCredentialMagicLink] = useState("");
  const [credentialLinkError, setCredentialLinkError] = useState("");
  const [credentialLinkExpiry, setCredentialLinkExpiry] = useState(null);
  const [credentialLinkEmailSent, setCredentialLinkEmailSent] = useState(false);
  const [credentialLinkCopied, setCredentialLinkCopied] = useState(false);
  const [credentialLinkLoading, setCredentialLinkLoading] = useState(false);

  // Get POS integration status
  const posIntegrationStatus = props.company.pos_integration || POS_INTEGRATION_NOT_INTEGRATED;

  // Grouped status sets for easy checks
  const CONNECTED_STATUSES = new Set([
    POS_INTEGRATION_TOAST_CONNECTED,
    POS_INTEGRATION_HEARTLAND_RETAIL_CONNECTED,
    POS_INTEGRATION_SQUARE_CONNECTED,
  ]);

  const PENDING_STATUSES = new Set([
    POS_INTEGRATION_TOAST_PENDING,
    POS_INTEGRATION_HEARTLAND_RETAIL_PENDING,
    POS_INTEGRATION_SQUARE_PENDING,
  ]);

  // Helper functions to determine status
  const isConnected = () => CONNECTED_STATUSES.has(posIntegrationStatus);
  const isPending = () => PENDING_STATUSES.has(posIntegrationStatus);


  // Get status display info
  const getStatusIcon = () => {
    if (isConnected()) {
      return <CheckCircleOutlineIcon style={{ fontSize: "18px", color: "#10b981" }} />;
    } else if (isPending()) {
      return <PendingIcon style={{ fontSize: "18px", color: "#3b82f6" }} />;
    } else {
      return <WarningAmberIcon style={{ fontSize: "18px", color: "#f59e0b" }} />;
    }
  };

  const getConnectionDescription = () => {
    if (isConnected()) {
      return "Your POS credentials are saved. Data syncs separately; review your locations when they appear.";
    } else if (isPending()) {
      return "Your POS integration is being set up. A Rightwork representative will complete the connection process.";
    } else {
      return "Connect your POS system to sync real-time data for accurate scheduling and reporting.";
    }
  };

  const handleOpenConnectPOSModal = () => {
    setShowConnectPOSModal(true);
  };

  const handleCloseConnectPOSModal = () => {
    setShowConnectPOSModal(false);
  };

  const handlePosModalOpen = () => setPosModalOpen(true);
  const handlePosModalClose = () => setPosModalOpen(false);
  const handleConnectedDetailsClose = () => setConnectedDetailsOpen(false);
  const canGenerateToastCredentialLink =
    props.isAdminForSelectedCompany === true &&
    !isConnected();

  React.useEffect(() => {
    credentialRequestGeneration.current += 1;
    setCredentialLinkLoading(false);
    setActionsAnchor(null);
    setShowCredentialLink(false);
    setCredentialRecipientEmail("");
    setCredentialMagicLink("");
    setCredentialLinkExpiry(null);
    setCredentialLinkError("");
    setCredentialLinkEmailSent(false);
    setCredentialLinkCopied(false);
  }, [props.company.id, props.isAdminForSelectedCompany, posIntegrationStatus]);

  const handleCreateToastCredentialLink = async () => {
    if (!canGenerateToastCredentialLink || !props.company?.id || credentialLinkLoading) {
      return;
    }

    setCredentialLinkLoading(true);
    setCredentialLinkError("");
    setCredentialMagicLink("");
    setCredentialLinkExpiry(null);
    setCredentialLinkEmailSent(false);
    setCredentialLinkCopied(false);

    const generation = credentialRequestGeneration.current;
    try {
      const axiosConfig = await getDefaultAxiosConfig();
      const response = await axios.post(
        API_create_toast_credential_link,
        {
          company_id: props.company.id,
          recipient_email: credentialRecipientEmail.trim(),
          send_email: credentialRecipientEmail.trim().length > 0,
        },
        axiosConfig
      );
      if (generation !== credentialRequestGeneration.current) return;
      const payload = response?.data?.redux?.payload || response?.data || {};
      setCredentialMagicLink(payload.magic_link || "");
      setCredentialLinkExpiry(payload.expires_at || null);
      setCredentialLinkEmailSent(Boolean(payload.email_sent));
    } catch (error) {
      if (generation !== credentialRequestGeneration.current) return;
      const message = error?.response?.data?.system?.message || "Unable to create Toast credential link.";
      setCredentialLinkError(message);
    } finally {
      if (generation === credentialRequestGeneration.current) setCredentialLinkLoading(false);
    }
  };

  const handleCopyToastCredentialLink = async () => {
    if (!credentialMagicLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(credentialMagicLink);
      setCredentialLinkCopied(true);
    } catch (error) {
      setCredentialLinkError("Unable to copy the link from this browser.");
    }
  };

  const posLinkedCount = Object.values(props.posLinkStatusMap || {}).filter(
    (entry) => entry?.hasPosLink
  ).length;

  React.useEffect(() => {
    if (!props.initialIntent) {
      return;
    }
    if (props.initialIntent === "connect_pos") {
      setShowConnectPOSModal(true);
    }
    if (props.initialIntent === "import_stores") {
      setPosModalOpen(true);
    }
    props.onIntentHandled?.();
  }, [props.initialIntent, props.onIntentHandled]);

  return (
    <SettingsPageLayout>
      <div className="space-y-6">
      <header><h1 className={settingsPageTitleClass}>Integrations</h1><p className="mt-1 text-sm text-slate-500">Manage your POS connection and linked locations.</p></header>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" aria-label="Point of sale connection">
        <div className="flex items-start justify-between gap-4 p-4 sm:flex-col">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Point of sale connection</h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{getConnectionDescription()}</p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${isConnected() ? "border-emerald-200 bg-emerald-50 text-emerald-700" : isPending() ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              {getStatusIcon()}{isConnected() ? "Connected" : isPending() ? "Integration pending" : "Not connected"}
            </span>
            {(isConnected() || !isPending()) && <button type="button" onClick={isConnected() ? () => setConnectedDetailsOpen(true) : handleOpenConnectPOSModal}
              className="rounded-lg border border-violet-200 px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
              {isConnected() ? "Connection details" : "Connect POS"}
            </button>}
            {canGenerateToastCredentialLink && <>
              <button type="button" aria-label="POS connection actions" title="POS connection actions"
                aria-haspopup="menu" aria-expanded={Boolean(actionsAnchor)} aria-controls={actionsAnchor ? actionsMenuId : undefined}
                onClick={(event) => setActionsAnchor(event.currentTarget)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
                <MoreHorizIcon fontSize="small" />
              </button>
              <Menu id={actionsMenuId} anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ "aria-label": "POS connection actions" }}
                PaperProps={{ sx: { mt: 0.5, border: "1px solid #e2e8f0", borderRadius: "8px", maxWidth: "calc(100vw - 32px)",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)", color: "#334155",
                  "& .MuiMenuItem-root": { fontSize: 14, py: 1, whiteSpace: "normal", "&:hover, &.Mui-focusVisible": { bgcolor: "#f5f3ff", color: "#6d28d9" } },
                  "& .MuiListItemIcon-root": { color: "inherit" } } }}>
                <MenuItem onClick={() => { setActionsAnchor(null); setShowCredentialLink(true); }}>
                  <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>Share Toast setup link
                </MenuItem>
              </Menu>
            </>}
          </div>
        </div>
      {props.posStores?.length > 0 && (
        <div className="mx-4 mb-4 flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex flex-wrap items-center gap-2 py-2 rounded-lg border border-amber-200 bg-amber-50 px-3 text-amber-700">
            <WarningIcon style={{ fontSize: "17px" }} />
            <span className="font-medium">{`${props.posStores?.length} locations not imported from POS`}</span>
            <button
              type="button"
              className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-white"
              onClick={handlePosModalOpen}
            >
              Review
            </button>
          </div>
        </div>
      )}

      {canGenerateToastCredentialLink && showCredentialLink && (
        <div data-sentry-block className="border-t border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-800">Secure Toast credential link</h3>
          <p className="mt-1 text-xs text-slate-600">Anyone with this one-time link can submit Toast credentials for this company. Share only with your Toast administrator. Generating another link replaces the previous one; connecting a POS also invalidates it.</p>
          <p className="mt-1 text-xs text-slate-500">Generate a one-time link for this company. Add an email to send it automatically.</p>
          <div className="mt-4 flex max-w-2xl items-end gap-3 sm:flex-col sm:items-stretch">
            <div className="min-w-0 flex-1">
              <label htmlFor={recipientEmailId} className="mb-1.5 block text-xs font-medium text-slate-600">Recipient email (optional)</label>
              <input
                id={recipientEmailId}
                type="email"
                autoComplete="email"
                value={credentialRecipientEmail}
                onChange={(event) => setCredentialRecipientEmail(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600"
                placeholder="client@example.com"
              />
            </div>
            <button
              type="button"
              onClick={handleCreateToastCredentialLink}
              disabled={credentialLinkLoading}
              className={`inline-flex shrink-0 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${
                credentialLinkLoading
                  ? "bg-slate-200 text-slate-500"
                  : "bg-violet-700 text-white hover:bg-violet-800"
              }`}
            >
              <LinkIcon style={{ fontSize: "16px", marginRight: "6px" }} />
              {credentialRecipientEmail.trim() ? "Generate and send" : "Generate link"}
            </button>
          </div>

          {credentialMagicLink && (
            <div className="mt-3 flex max-w-2xl items-center gap-2 sm:flex-col sm:items-stretch">
              <input
                readOnly
                aria-label="Generated credential link"
                value={credentialMagicLink}
                className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none"
              />
              <button
                type="button"
                onClick={handleCopyToastCredentialLink}
                className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <ContentCopyIcon style={{ fontSize: "16px", marginRight: "6px" }} />
                {credentialLinkCopied ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          {credentialLinkExpiry && <p className="mt-2 text-xs text-slate-600">Expires {new Date(credentialLinkExpiry).toLocaleString()}.</p>}
          {credentialLinkEmailSent && (
            <p role="status" className="mt-2 text-xs text-emerald-700">Email sent.</p>
          )}

          {credentialMagicLink && credentialRecipientEmail.trim() && !credentialLinkEmailSent && (
            <p className="mt-2 text-xs text-amber-700">
              Link created. Email was not sent, so copy the link manually.
            </p>
          )}

          {credentialLinkError && (
            <p role="alert" className="mt-2 text-xs text-rose-700">{credentialLinkError}</p>
          )}
        </div>
      )}

      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" aria-label="Location connections">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Location connections</h2>
          <p className="mt-1 text-xs text-slate-500">Review the integration data available for each location.</p>
        </div>
        <div className="overflow-x-auto" role="region" aria-label="Location integrations table" tabIndex={0}>
          <table className="w-full min-w-[520px] border-collapse text-left text-sm">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th scope="col" className="w-1/3 px-4 py-3 text-xs font-semibold text-slate-600">Location</th>
                <th scope="col" className="border-l border-slate-200 px-4 py-3 text-xs font-semibold text-slate-600">Integration data</th>
                <th scope="col" className="w-36 border-l border-slate-200 px-4 py-3 text-right text-xs font-semibold text-slate-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {props.integrationsData?.map((location) => <IntegrationsTableRow
                key={location.id} location={location} update_integrations_info={props.update_integrations_info} posLinkMap={props.posLinkStatusMap}
              />)}
              {!props.integrationsData?.length && <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">No locations available.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Connect POS Modal */}
      <ConnectPOSModal
        open={showConnectPOSModal}
        onClose={handleCloseConnectPOSModal}
        companyId={props.company.id}
        onIntegrationComplete={props.onIntegrationComplete}
      />

      <ConnectedPOSDetailsModal
        open={connectedDetailsOpen}
        onClose={handleConnectedDetailsClose}
        company={props.company}
        posStores={props.posStores}
        posLinkedCount={posLinkedCount}
        onRefreshLocations={props.refresh_pos_locations}
        onOpenImportStores={() => {
          setConnectedDetailsOpen(false);
          setPosModalOpen(true);
        }}
        isRefreshing={props.isRefreshingPosLocations}
      />

      <ImportStoresModal
        open={posModalOpen}
        onClose={handlePosModalClose}
        posStores={props.posStores}
        existingStores={props.integrationsData}
        link_or_create_store_from_pos={props.link_or_create_store_from_pos}
        archive_pos_store={props.archive_pos_store}
      />
      </div>
    </SettingsPageLayout>
  )
}

export default IntegrationsDisplay
